import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

export const REST_PRESETS = [
  { label: '30с', seconds: 30 },
  { label: '1м', seconds: 60 },
  { label: '1:30', seconds: 90 },
  { label: '2м', seconds: 120 },
  { label: '3м', seconds: 180 },
];

// Generate loud beep sound using Web Audio API
function playBeep() {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    
    const playTone = (startTime: number) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Higher frequency for better audibility
      oscillator.frequency.value = 1000;
      oscillator.type = 'sine';
      
      // Louder volume, longer duration
      gainNode.gain.setValueAtTime(0.8, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.1, startTime + 0.3);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.3);
    };
    
    const now = audioContext.currentTime;
    // 5 beeps for better notification
    playTone(now);
    playTone(now + 0.4);
    playTone(now + 0.8);
    playTone(now + 1.2);
    playTone(now + 1.6);
    
    // Vibration pattern
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 200, 100, 200, 100, 200]);
    }
  } catch (e) {
    console.warn('Audio not supported:', e);
  }
}

interface TimerContextType {
  isOpen: boolean;
  isRunning: boolean;
  isFinished: boolean;
  timeLeft: number;
  totalTime: number;
  progress: number;
  openTimer: () => void;
  closeTimer: () => void;
  startTimer: (seconds: number) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  resetTimer: () => void;
  addTime: (seconds: number) => void;
}

const TimerContext = createContext<TimerContextType | null>(null);

export function TimerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(90);
  const [totalTime, setTotalTime] = useState(90);
  
  // Timestamp-based timing - survives app minimization
  const endTimeRef = useRef<number>(0);
  const pausedTimeLeftRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  const stopLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    if (!isRunning) return;
    
    const now = Date.now();
    const remaining = Math.max(0, Math.ceil((endTimeRef.current - now) / 1000));
    
    setTimeLeft(remaining);
    
    if (remaining <= 0) {
      stopLoop();
      setIsRunning(false);
      setIsFinished(true);
      playBeep();
      return;
    }
    
    rafRef.current = requestAnimationFrame(tick);
  }, [isRunning, stopLoop]);

  const startTimer = useCallback((seconds: number) => {
    stopLoop();
    setTotalTime(seconds);
    setTimeLeft(seconds);
    setIsFinished(false);
    setIsRunning(true);
    setIsOpen(true);
    
    endTimeRef.current = Date.now() + seconds * 1000;
  }, [stopLoop]);

  const pauseTimer = useCallback(() => {
    stopLoop();
    pausedTimeLeftRef.current = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
    setIsRunning(false);
  }, [stopLoop]);

  const resumeTimer = useCallback(() => {
    if (pausedTimeLeftRef.current > 0 && !isFinished) {
      endTimeRef.current = Date.now() + pausedTimeLeftRef.current * 1000;
      setIsRunning(true);
    }
  }, [isFinished]);

  const resetTimer = useCallback(() => {
    stopLoop();
    setIsRunning(false);
    setTimeLeft(totalTime);
    setIsFinished(false);
    endTimeRef.current = 0;
    pausedTimeLeftRef.current = 0;
  }, [stopLoop, totalTime]);

  const addTime = useCallback((seconds: number) => {
    setTimeLeft(prev => prev + seconds);
    setTotalTime(prev => prev + seconds);
    if (isRunning) {
      endTimeRef.current += seconds * 1000;
    } else {
      pausedTimeLeftRef.current += seconds;
    }
  }, [isRunning]);

  const closeTimer = useCallback(() => {
    stopLoop();
    setIsRunning(false);
    setIsOpen(false);
    setIsFinished(false);
    setTimeLeft(totalTime);
    endTimeRef.current = 0;
    pausedTimeLeftRef.current = 0;
  }, [stopLoop, totalTime]);

  const openTimer = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Timer loop using requestAnimationFrame
  useEffect(() => {
    if (isRunning) {
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => stopLoop();
  }, [isRunning, tick, stopLoop]);

  // Also check on visibility change (when app comes back from background)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isRunning) {
        // Force recalculation when app becomes visible
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((endTimeRef.current - now) / 1000));
        setTimeLeft(remaining);
        
        if (remaining <= 0 && !isFinished) {
          setIsRunning(false);
          setIsFinished(true);
          playBeep();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRunning, isFinished]);

  const progress = totalTime > 0 ? ((totalTime - timeLeft) / totalTime) * 100 : 0;

  return (
    <TimerContext.Provider value={{
      isOpen,
      isRunning,
      isFinished,
      timeLeft,
      totalTime,
      progress,
      openTimer,
      closeTimer,
      startTimer,
      pauseTimer,
      resumeTimer,
      resetTimer,
      addTime,
    }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}
